import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import action from 'app/common/lib/action'
import { ReportSection, ResetLink } from 'app/common/templates-next/report-template'
import ReportFilterForm from 'app/common/templates-next/report-template/report-filter-form'
import ReportFilterLayout from 'app/common/templates-next/report-template/report-filter-layout'
import { MailingBox, canMailingOnly } from 'app/common/ui-next/_filter-compilations/mailing'
import { detailsBoxID } from 'app/common/ui-next/_filter-compilations/details'
import { ReportStatusIndicator } from 'app/components/common/reports/v3/ReportStatusIndicator'
import { useReportPerform } from 'app/dataProviders/reports/hooks'
import { ReportType } from 'app/dataTypes/reports'
import {
  SUBSIDIES_SET_REPORT_ID,
  SUBSIDIES_VIEW_PARAMS_CREATE,
} from 'app/actions/reports/subsidies'

import {
  ClassTypeFilter,
  ClassesListFilter,
  DetailsFilter,
  GroupFilter,
  EducationLevelsFilter,
  EmployeeListFilter,
  OrgTypesFilter,
  OrgsFilter,
  ParallelsFilter,
  PeriodFilter,
  SchoolYearFilter,
  StudentListFilter,
  SubsidyTypeFilter,
  AllowancesFilter,
  SumsAndKitsFilter,
  ViewTypeFilter,
} from './filterItems'
import { DetailedSubsidyFilterKey as ID } from './filterItems/types'
import { filterInit, resetToDefaultAll } from './filter-init'
import styles from './ReportFilter.scss'

export const Filters = () => {
  const dispatch = useDispatch()
  const [mailingResetKey, setMailingResetKey] = useState(0)
  const [pendingReportID, setPendingReportID] = useState<string | null>(null)
  const onReportId = useCallback((reportID: string | null) => {
    setPendingReportID(reportID)
  }, [])
  const { perform } = useReportPerform({
    onReportId,
    isMailingOnly: (formData) => !!canMailingOnly(formData),
  })

  useEffect(() => filterInit(), [])

  const handleResetAll = useCallback(() => {
    resetToDefaultAll()
    setMailingResetKey((value) => value + 1)
  }, [])

  const handleReportReady = useCallback(() => {
    if (!pendingReportID) return
    dispatch(action(SUBSIDIES_SET_REPORT_ID, Number(pendingReportID)))
  }, [dispatch, pendingReportID])

  const handleSubmit = useCallback(
    async (formData: FormData) => {
      const orgs = formData.getAll(ID.Orgs)
      dispatch(
        action(SUBSIDIES_VIEW_PARAMS_CREATE, {
          details: !!formData.get(ID.Details),
          multipleOrgs: orgs.length !== 1,
          viewType: (formData.get(ID.ViewType) as string) || 'sum',
          subsidyType: (formData.get(ID.SubsidyType) as string) || 'all',
        }),
      )
      await perform({ formData, reportType: ReportType.SubsidiesDetailed })
    },
    [dispatch, perform],
  )

  return (
    <ReportSection
      header="Фильтры"
      headerBar={<ResetLink onClick={handleResetAll}>Сбросить все фильтры</ResetLink>}
      resetStickyShift={false}
      skipPadding
    >
      <div className={styles.formContent}>
        <ReportFilterForm onSubmit={handleSubmit} submitButtonTitle="Сформировать">
          {(pending) => (
            <ReportFilterLayout
              column1={
                <>
                  <OrgTypesFilter id={ID.OrgTypes} pending={pending} />
                  <OrgsFilter id={ID.Orgs} pending={pending} />
                  <ClassTypeFilter id={ID.ClassType} pending={pending} />
                  <ParallelsFilter id={ID.Parallels} pending={pending} />
                  <EducationLevelsFilter id={ID.EducationLevels} pending={pending} />
                  <ClassesListFilter id={ID.Classes} pending={pending} />
                  <StudentListFilter id={ID.Students} pending={pending} />
                  <EmployeeListFilter id={ID.Employees} pending={pending} />
                  <DetailsFilter id={ID.Details} pending={pending} />
                  <GroupFilter id={ID.Group} pending={pending} />
                </>
              }
              column2={
                <>
                  <SchoolYearFilter id={ID.SchoolYear} pending={pending} />
                  <PeriodFilter id={ID.Period} pending={pending} />
                  <SubsidyTypeFilter id={ID.SubsidyType} pending={pending} />
                  <AllowancesFilter id={ID.Allowances} pending={pending} />
                  <SumsAndKitsFilter id={ID.SumsAndKits} pending={pending} />
                  <ViewTypeFilter id={ID.ViewType} pending={pending} />
                  <MailingBox
                    key={mailingResetKey}
                    visibleFromBox={detailsBoxID}
                    disabled={pending}
                  />
                </>
              }
            />
          )}
        </ReportFilterForm>
        {pendingReportID && (
          <ReportStatusIndicator
            reportID={pendingReportID}
            onFail={() => setPendingReportID(null)}
            onSuccess={handleReportReady}
          />
        )}
      </div>
    </ReportSection>
  )
}
